import { PartnershipTab } from "./partners/Trust.jsx";
import { useMemo, useState } from "react";
import { api, money, day } from "../api.js";
import { useResource } from "./useResource.js";
import { Empty, shortName } from "./common.jsx";
import PartnerDossier from "./partners/PartnerDossier.jsx";
import { sortPartners, PARTNER_TEXT_SORT, PARTNER_DEFAULT_SORT } from "../../../lib/partners-view.js";

// Контрагенти: один рядок на код — продавець в отриманих ПН/РК, покупець у виданих, а також коди без ПН, які
// перевірили чи додали до списку компанії. Картка-досьє: реєстри ДПС з ІПН, що перевірено, фінзвітність за п'ять
// років і офіційна ризиковість (стан джерел — чесно), наша співпраця (docs/counterparties-plan.md, CP-01/CP-02)
const TONE = { ok: ["ДПС: без зауважень", "ok"], warn: ["ДПС: перевірено не все", "warn"], bad: ["ДПС: ризик", "bad"] };
const RANK = { preparer: 1, submitter: 2, owner: 3 };
const canEdit = (company) => (RANK[company?.role] || 0) >= 1;

// Бізнес-партнер і ризиковість за СМКОР (дані ДПС за згодою партнера)
function PartnerBadges({ p }) {
  return (
    <span className="cp-badges">
      <span className="badge badge-info">Бізнес-партнер</span>
      {p.smkor === "risky" && <span className="badge badge-bad">Ризиковий{p.since ? ` з ${String(p.since).slice(0, 10).split("-").reverse().join(".")}` : ""}</span>}
      {p.smkor === "was" && <span className="badge badge-warn">Був ризиковим</span>}
      {p.smkor === "ok" && <span className="badge badge-ok">Не ризиковий</span>}
    </span>
  );
}

export default function Partners({ companyId, company, sub, versions }) {
  if (sub && sub !== "partnership") return <PartnerDossier companyId={companyId} company={company} tin={sub} versions={versions} />;
  const tab = sub === "partnership" ? "partnership" : "list";
  // Скільки вхідних пропозицій чекає — лічильник на вкладці
  const trust = useResource(`/api/companies/${companyId}/trust`, `${sub}`);
  const offers = trust.data?.incoming?.length || 0;
  return (
    <>
      <div className="section-head"><div><h2>Контрагенти</h2></div></div>
      <div className="tabs reports-tabs" role="tablist" aria-label="Контрагенти">
        <a role="tab" aria-selected={tab === "list"} className={`tab${tab === "list" ? " active" : ""}`} href={`#/c/${companyId}/partners`}>Контрагенти</a>
        <a role="tab" aria-selected={tab === "partnership"} className={`tab${tab === "partnership" ? " active" : ""}`} href={`#/c/${companyId}/partners/partnership`}>Бізнес-партнери{offers ? <span className="count">{offers}</span> : null}</a>
      </div>
      {tab === "partnership" ? <PartnershipTab companyId={companyId} company={company} /> : <PartnerList companyId={companyId} company={company} versions={versions} />}
    </>
  );
}

function CheckAny({ companyId, company, onAdded }) {
  const [tin, setTin] = useState("");
  const [busy, setBusy] = useState(false), [msg, setMsg] = useState(null);
  const ok = /^\d{8}$|^\d{10}$/.test(tin);
  const go = (e) => { e.preventDefault(); if (ok) location.hash = `#/c/${companyId}/partners/${tin}?check=1`; };
  const add = async () => {
    setBusy(true); setMsg(null);
    try { await api(`/api/companies/${companyId}/partners`, { method: "POST", body: { tin } }); setMsg({ ok: `Код ${tin} додано до списку` }); setTin(""); onAdded?.(); }
    catch (e) { setMsg({ error: e.message }); }
    finally { setBusy(false); }
  };
  return (
    <form className="check-any" onSubmit={go} role="search" aria-label="Перевірка контрагента">
      <label className="field field-inline">
        <span>Контрагент за кодом</span>
        <input inputMode="numeric" maxLength={10} placeholder="ЄДРПОУ або РНОКПП" value={tin}
          onChange={(e) => setTin(e.target.value.replace(/\D/g, ""))} aria-describedby="check-any-hint" />
      </label>
      <button type="submit" className="btn btn-sm" disabled={!ok}>Перевірити</button>
      {canEdit(company) && <button type="button" className="btn btn-sm btn-ghost" disabled={!ok || busy} onClick={add}>Додати до списку</button>}
      <span id="check-any-hint" className="muted small">Перевірка — реєстри ДПС під вашою сесією; «Додати» — до списку компанії без ПН (потенційний постачальник чи покупець)</span>
      {msg && <p className={msg.error ? "error" : "muted small"} role={msg.error ? "alert" : "status"}>{msg.error || msg.ok}</p>}
    </form>
  );
}

// Заголовок колонки з сортуванням (як у «Русі товарів»): перший клік — від більшого (назва — за алфавітом), далі — навпаки
function SortTh({ k, sort, setSort, children, className = "num" }) {
  const on = sort.key === k, first = PARTNER_TEXT_SORT.has(k) ? "asc" : "desc";
  const next = () => setSort(on ? { key: k, dir: sort.dir === "desc" ? "asc" : "desc" } : { key: k, dir: first });
  const hint = (on ? (sort.dir === "desc" ? "asc" : "desc") : first) === "desc" ? "від більшого до меншого" : PARTNER_TEXT_SORT.has(k) ? "за алфавітом" : "від меншого до більшого";
  return (
    <th className={className} aria-sort={on ? (sort.dir === "desc" ? "descending" : "ascending") : "none"}>
      <button type="button" className="th-sort" onClick={next} title={`Сортувати ${hint}`}>
        {children}<span aria-hidden="true">{on ? (sort.dir === "desc" ? " ↓" : " ↑") : " ↕"}</span></button>
    </th>
  );
}

function PartnerList({ companyId, company, versions }) {
  const [sort, setSort] = useState(PARTNER_DEFAULT_SORT);
  const [added, setAdded] = useState(0);
  const r = useResource(`/api/companies/${companyId}/partners`, `${versions.erpn}:${added}`);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    return sortPartners((r.data?.partners || []).filter((p) => (role === "all" || (role === "none" ? !p.in_count && !p.out_count : role === "in" ? p.in_count : p.out_count))
      && (!t || p.tin.includes(t) || String(p.name || "").toLowerCase().includes(t))), sort);
  }, [r.data, q, role, sort]);
  const all = r.data?.partners || [];
  const totals = list.reduce((a, p) => ({ inT: a.inT + Number(p.in_total), outT: a.outT + Number(p.out_total) }), { inT: 0, outT: 0 });
  return (
    <>
      <p className="notice">З ПН/РК (продавці в отриманих, покупці у виданих) і коди без ПН, які ви перевірили чи додали</p>
      <CheckAny companyId={companyId} company={company} onAdded={() => setAdded((x) => x + 1)} />
      <div className="card">
        <div className="row toolbar">
          <div className="tabs" role="group" aria-label="Роль контрагента">
            {[["all", `Усі (${all.length})`], ["in", `Постачальники (${all.filter((p) => p.in_count).length})`], ["out", `Покупці (${all.filter((p) => p.out_count).length})`], ["none", `Без ПН (${all.filter((p) => !p.in_count && !p.out_count).length})`]].map(([k, t]) => (
              <button key={k} type="button" className={role === k ? "tab active" : "tab"} aria-pressed={role === k} onClick={() => setRole(k)}>{t}</button>
            ))}
          </div>
          <input className="search" type="search" aria-label="Пошук контрагента" placeholder="Назва або код" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {r.pending ? <p className="muted" role="status">Завантаження…</p>
          : r.error && !r.data ? <p className="error">Не вдалося завантажити: {r.error.message}</p>
          : !all.length ? <Empty>Контрагентів ще немає — вони з'являться після синку ЄРПН або коли ви перевірите чи додасте код вище.</Empty>
          : !list.length ? <Empty>Нічого не знайдено.</Empty> : (
            <div className="table-wrap">
              <table>
                <thead><tr><SortTh k="name" sort={sort} setSort={setSort} className="">Контрагент</SortTh><SortTh k="in" sort={sort} setSort={setSort}>Придбано, з ПДВ</SortTh>
                  <SortTh k="out" sort={sort} setSort={setSort}>Продано, з ПДВ</SortTh><SortTh k="docs" sort={sort} setSort={setSort}>ПН/РК</SortTh>
                  <SortTh k="last" sort={sort} setSort={setSort} className="">Остання ПН</SortTh><SortTh k="check" sort={sort} setSort={setSort} className="">Реєстри ДПС</SortTh></tr></thead>
                <tbody>
                  {list.map((p) => {
                    const open = () => { location.hash = `#/c/${companyId}/partners/${p.tin}`; };
                    const [label, tone] = p.check ? TONE[p.check.tone] || ["—", "idle"] : ["ДПС: не перевіряли", "idle"];
                    return (
                      <tr key={p.tin} tabIndex={0} onClick={open} onKeyDown={(e) => { if (e.key === "Enter") open(); }}>
                        <td className="wrap-cell"><b>{shortName(p.name) || "—"}</b>{p.partner && <PartnerBadges p={p.partner} />}<br /><span className="muted small">{p.tin}{p.ipn ? ` · ІПН ${p.ipn}` : ""}{p.listed ? " · у списку" : ""}{p.accounts ? ` · рахунків: ${p.accounts}` : ""}</span>{p.note && <><br /><span className="muted small">{p.note}</span></>}</td>
                        <td className="num">{p.in_count ? money(p.in_total) : "—"}</td>
                        <td className="num">{p.out_count ? money(p.out_total) : "—"}</td>
                        <td className="num">{p.in_count + p.out_count}</td>
                        <td>{p.last_date ? day(p.last_date) : "—"}</td>
                        <td><span className={`badge badge-${tone}`}>{label}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot><tr><th>Разом ({list.length})</th><th className="num">{money(totals.inT)}</th><th className="num">{money(totals.outT)}</th><td colSpan={3} /></tr></tfoot>
              </table>
            </div>
          )}
      </div>
    </>
  );
}
